import { ChatComposerTextArea, type ChatComposerTextAreaProps } from '@mui/x-chat';
import { chatSelectors, useChatStore } from '@mui/x-chat/headless';
import { useRef } from 'react';

import { isDefined } from 'common';

/**
 * The composer input, with shell-style recall of previously sent messages.
 *
 * ArrowUp on an empty composer fills it with the last message the user sent; pressing again walks
 * further back, and ArrowDown walks forward until the composer is empty again. Editing the recalled
 * text ends the walk: the next ArrowUp moves the caret as usual.
 */
export function AgenticChatComposerInput(props: ChatComposerTextAreaProps) {
  const store = useChatStore();
  const recalledRef = useRef<{ index: number; text: string }>(undefined);

  return (
    <ChatComposerTextArea
      {...props}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
          return;
        }
        const value = chatSelectors.composerValue(store.state);
        const recalled = recalledRef.current;
        const isWalking = isDefined(recalled) && recalled.text === value;
        if (!isWalking && value !== '') {
          return;
        }

        const sentTexts = chatSelectors
          .messages(store.state)
          .filter((message) => message.role === 'user')
          .map((message) =>
            message.parts
              .map((part) => (part.type === 'text' ? part.text : ''))
              .join('')
              .trim()
          )
          .filter((text) => text !== '')
          .reverse();
        const index = (isWalking ? recalled.index : -1) + (event.key === 'ArrowUp' ? 1 : -1);
        if (index >= sentTexts.length || index < -1) {
          return;
        }

        event.preventDefault();
        const text = index === -1 ? '' : sentTexts[index];
        recalledRef.current = { index, text };
        store.setComposerValue(text);
      }}
    />
  );
}
